import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiCall } from '../utils/api';
import useUserPermissions from '../hooks/useUserPermissions';
import { config } from '../config/appConfig';

function Runbook() {
  const navigate = useNavigate();
  const { canModify, isLoading } = useUserPermissions();
  const [tribe, setTribe] = useState('');
  const [solution, setSolution] = useState('');
  const [runbookTitle, setRunbookTitle] = useState('');
  const [saving, setSaving] = useState(false);
  
  const tribes = config.tribes;
  const [solutions, setSolutions] = useState([]);

  // Redirect users without modify permissions
  useEffect(() => {
    if (!isLoading && !canModify()) {
      alert('You do not have permission to create runbooks. Redirecting to runbook list.');
      navigate('/runbook_list');
      return;
    }
  }, [canModify, isLoading, navigate]);

  // Update solutions when tribe changes
  useEffect(() => {
    if (tribe) {
      // Get solutions for selected tribe from config
      const tribeSolutions = config.solutions[tribe];
      if (tribeSolutions) {
        // Extract solution names (keys) from the tribe solutions object
        setSolutions(Object.keys(tribeSolutions));
      } else {
        setSolutions([]);
      }
    } else {
      setSolutions([]);
    }
    setSolution('');
  }, [tribe]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Validate runbook title length (minimum 5 characters)
    if (runbookTitle.trim().length < 5) {
      alert('Runbook title must be at least 5 characters long.');
      return;
    }
    
    const payload = {
      tribe,
      solution,
      runbookTitle,
      // modifiedBy will be set automatically by backend from JWT token
    };

    console.log('[CREATE] Creating runbook...', payload);

    try {
      setSaving(true);
      const response = await apiCall('/runbooks', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      
      console.log('[CREATE] Runbook created successfully:', response);
      alert('Runbook created successfully!');
      navigate('/runbook_list');
    } catch (error) {
      console.error('[CREATE] Error creating runbook:', error);
      console.error('[CREATE] Error message:', error.message);
      console.error('[CREATE] Error details:', error.details);
      
      // Show detailed error message
      let errorMessage = 'Failed to create runbook:\n\n';
      errorMessage += error.message || 'Unknown error';
      
      if (error.details) {
        errorMessage += '\n\nValidation errors:';
        if (Array.isArray(error.details)) {
          error.details.forEach(detail => {
            errorMessage += '\n• ' + detail;
          });
        } else if (typeof error.details === 'object') {
          errorMessage += '\n' + JSON.stringify(error.details, null, 2);
        } else {
          errorMessage += '\n' + error.details;
        }
      }
      
      alert(errorMessage);
      setSaving(false);
    }
  };

  const handleCancel = () => {
    navigate('/runbook_list');
  };


  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2>Create Runbook</h2>
        <button 
          className="btn btn-secondary"
          onClick={() => navigate('/runbook_list')}
          disabled={saving}
        >
          Back to Runbook List
        </button>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="card mb-4">
          <div className="card-header bg-primary text-white">
            <h5 className="mb-0">Basic Information</h5>
          </div>
          <div className="card-body">
            <div className="row mb-3">
              <div className="col-md-6">
                <label htmlFor="tribe" className="form-label">
                  <strong>Tribe <span className="text-danger">*</span></strong>
                </label>
                <select 
                  id="tribe" 
                  name="tribe" 
                  className="form-select"
                  value={tribe} 
                  onChange={(e) => setTribe(e.target.value)} 
                  required
                  disabled={saving}
                >
                  <option value="">Select Tribe</option>
                  {tribes.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              
              <div className="col-md-6">
                <label htmlFor="solution" className="form-label">
                  <strong>Solution <span className="text-danger">*</span></strong>
                </label>
                <select 
                  id="solution" 
                  name="solution" 
                  className="form-select"
                  value={solution} 
                  onChange={(e) => setSolution(e.target.value)} 
                  required
                  disabled={saving || !tribe}
                >
                  <option value="">Select Solution</option>
                  {solutions.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {!tribe && (
                  <small className="text-muted">Please select a tribe first</small>
                )}
              </div>
            </div>

            <div className="mb-3">
              <label htmlFor="runbook_title" className="form-label">
                <strong>Runbook Title <span className="text-danger">*</span></strong>
              </label>
              <input
                type="text"
                id="runbook_title"
                name="runbook_title"
                className="form-control"
                value={runbookTitle}
                onChange={(e) => setRunbookTitle(e.target.value)}
                placeholder="Enter runbook title (minimum 5 characters)"
                minLength={5}
                maxLength={256}
                required
                disabled={saving}
              />
              <small className="text-muted">
                {runbookTitle.length}/256 characters (minimum 5 required)
              </small>
            </div>

            <div className="alert alert-info">
              <i className="bi bi-info-circle me-2"></i>
              <strong>Note:</strong> After creating the runbook, you can add steps with images and tags in the edit screen.
            </div>
          </div>
        </div>

        <div className="d-flex gap-2 mb-4">
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <span className="spinner-border spinner-border-sm me-2"></span>
                Creating...
              </>
            ) : (
              <>
                <i className="bi bi-plus-circle me-1"></i>
                Create Runbook
              </>
            )}
          </button>
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={handleCancel}
            disabled={saving}
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

export default Runbook;